import * as React from 'react';
import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import HC_exporting from 'highcharts/modules/exporting';
import HC_offlineExporting from 'highcharts/modules/offline-exporting';
import HC_patternFill from 'highcharts/modules/pattern-fill';

import { colors } from '@alixpartners/ui-components';

import { getFormattedPrice } from 'utils/number';
import { applyMaxPointWidthWrapper } from './utils';

import DetailsChartHighlightBlock from './DetailsChartHighlightBlock';

import {
	Wrapper,
	ChartWrapper,
	UnitsLabelBlock,
	NoDataAvailableContainer,
	NoDataAvailableLabel,
} from './styles';

HC_exporting(Highcharts);
HC_offlineExporting(Highcharts);
HC_patternFill(Highcharts);
applyMaxPointWidthWrapper(Highcharts);

export const CHART_TYPES = {
	waterfall: 'waterfall',
	line: 'line',
	bar: 'bar',
	scatter: 'scatter',
	stackedBar: 'stackedBar',
	stackedBar100: 'stackedBar100',
};

const CHART_TYPE_MAP = {
	[CHART_TYPES.line]: 'line',
	[CHART_TYPES.bar]: 'column',
	[CHART_TYPES.scatter]: 'scatter',
	[CHART_TYPES.stackedBar]: 'column',
	[CHART_TYPES.stackedBar100]: 'column',
	[CHART_TYPES.waterfall]: 'waterfall',
};

const TOOLTIP_COUNT_IN_COL = 5;

export const DetailsChart = props => {
	const {
		categories,
		series,
		currency,
		currencySymbol,
		scaleSymbol,
		type,
		setChartRef,
		pointsColorsForSeries,
		isPptExport,
		showAllSegments,
		showNegativeSpend,
		showNetSpend,
		hideDecimals: hideDecimalsProp,
		waterfallFilter,
		categoriesNames,
	} = props;

	let processedSeries = series;
	let unitsLabel = `${currency}${currencySymbol},${scaleSymbol}`.toUpperCase();

	let formatPrice = (value, hideCurrency, hideDecimals) => {
		const sign = value < 0 ? '-' : '';
		const formattedPrice =
			sign +
			getFormattedPrice(value, scaleSymbol, hideDecimalsProp || hideDecimals);

		return hideCurrency ? formattedPrice.slice(0, -1) : formattedPrice;
	};

	if (type === CHART_TYPES.stackedBar100) {
		unitsLabel = '%';
		formatPrice = value => (hideDecimalsProp ? Math.round(value) : value) + '%';
	} else if (type === CHART_TYPES.line) {
		const selectedConfig = {
			lineWidth: 5,
			className: 'selected-series',
		};
		processedSeries = series.map(
			({ selected, ...rest }) =>
				selected ? { ...rest, ...selectedConfig } : rest
		);
	}

	processedSeries = processedSeries.map(({ data, ...rest }, i) => {
		const res = {
			data: data.map((value, j) => ({
				name: categories[j],
				y: value,
				color: pointsColorsForSeries[i][j],
			})),
			...rest,
			color: pointsColorsForSeries[i][0],
		};

		return res;
	});

	const colCount = series.length > TOOLTIP_COUNT_IN_COL ? 2 : 1;
	const showLegend = processedSeries.length > 1;
	const handleChartRedraw = function() {
		if (!chartRef.current || !chartRef.current.chart) {
			return;
		}

		if (highlightBlockHeight !== chartRef.current.chart.plotHeight) {
			setHighlightBlockHeight(chartRef.current.chart.plotHeight);
		}
		if (highlightBlockWidth !== chartRef.current.chart.plotWidth) {
			setHighlightBlockWidth(chartRef.current.chart.plotWidth);
		}
	};

	const svgElementClassName = isPptExport
		? 'details-chart-ppt-export'
		: 'details-chart';

	const chartOptions = {
		exporting: {
			buttons: {
				contextButton: {
					enabled: false,
				},
			},
		},
		chart: {
			style: {
				fontFamily: isPptExport ? 'Verdana' : 'Roboto',
			},
			height: 380,
			type: type ? CHART_TYPE_MAP[type] || type : CHART_TYPE_MAP.bar,
			backgroundColor: 'rgba(0,0,0,0)',
			events: {
				load: function({ target }) {
					target.container.style.overflow = 'visible';
					target.container.firstChild.classList.add(svgElementClassName);
				},
				redraw: handleChartRedraw,
			},
		},
		title: {
			text: null,
		},
		xAxis: {
			categories,
			crosshair:
				type === CHART_TYPES.line || type === CHART_TYPES.scatter
					? {
							color: colors.grey7,
							dashStyle: 'dot',
							width: 1,
					  }
					: null,
			labels: {
				style: {
					fontSize: '11px',
					fontWeight: '400',
					lineHeight: '13px',
					letterSpacing: 0,
					color: colors.grey8,
				},
			},
			lineColor: colors.black,
		},
		legend: {
			enabled: showLegend,
			symbolRadius: 0,
			symbolHeight: 16,
			symbolPadding: 8,
			itemDistance: 32,
			itemMarginBottom: 8,
			align: 'center',
			itemStyle: {
				color: colors.grey8,
				cursor: 'pointer',
				fontSize: '11px',
				fontWeight: 'normal',
				textOverflow: 'ellipsis',
			},
		},
		yAxis: {
			labels: {
				formatter: function() {
					return `<strong>${formatPrice(this.value, true, true)}</strong>`;
				},
			},
			useHTML: true,
			lineWidth: 1,
			lineColor: colors.black,
			gridLineWidth: 0,
			minorGridLineWidth: 0,
			stackLabels: {
				enabled: false,
			},
			title: {
				enabled: false,
			},
			plotLines: [
				{
					value: 0,
					width: 1,
					color: colors.black,
					zIndex: 10,
				},
			],

			...(type === CHART_TYPES.stackedBar100
				? {
						max: 100,
						min: 0,
				  }
				: {}),
		},
		credits: {
			enabled: false,
		},
		tooltip: {
			enabled: false,
		},
		series: processedSeries,
		plotOptions: {
			line: {
				states: {
					hover: {
						symbol: 'circle',
					},
				},
			},
			series: {
				pointPadding: 0,
				dataLabels: {
					enabled: !showAllSegments,
					className: 'bar-label',
					style: {
						fontSize: '11px',
						fontWeight: '500',
					},
					formatter: function() {
						return `${formatPrice(this.y, true, false)}`;
					},
					overflow: 'none',
					crop: false,
				},
				marker: {
					enabled: type === CHART_TYPES.scatter,
					symbol: 'circle',
				},
				states: {
					hover: {
						enabled: type === CHART_TYPES.scatter || type === CHART_TYPES.line,
						hover: {
							// TODO: investigate hover effect
							brightness: -0.1,
						},
						marker: {
							enabled: true,
							symbol: 'circle',
							fillColor: colors.white,
						},
					},
				},
				events: {},
			},
			column:
				type === CHART_TYPES.stackedBar || type === CHART_TYPES.stackedBar100
					? {
							stacking:
								type === CHART_TYPES.stackedBar100 ? 'percent' : 'normal',
							dataLabels: {
								enabled: false,
							},
					  }
					: {},
			scatter: {
				marker: {
					radius: 5,
					states: {
						hover: {
							enabled: true,
							lineColor: 'rgb(100,100,100)',
						},
					},
				},
				states: {
					hover: {
						marker: {
							enabled: false,
						},
					},
				},
			},
		},
	};

	const [highlightBlockHeight, setHighlightBlockHeight] = React.useState(0);
	const [highlightBlockWidth, setHighlightBlockWidth] = React.useState(0);
	const wrapperRef = React.useRef(null);
	const chartRef = React.useRef(null);

	// eslint-disable-next-line react-hooks/exhaustive-deps
	React.useLayoutEffect(() => {
		if (chartRef.current && chartRef.current.chart) {
			setChartRef && setChartRef(chartRef);

			setHighlightBlockHeight(chartRef.current.chart.plotHeight);
		}
	});

	const showNoDataAvailable = !categories || categories.length === 0;
	const chart = chartRef.current && chartRef.current.chart;
	const refreshKey = `key_${type}_${showNegativeSpend}_${showNetSpend}`;

	let selectedLeafIndex = -1;
	if (waterfallFilter && waterfallFilter.length) {
		waterfallFilter.forEach(filterItem => {
			if (selectedLeafIndex === -1) {
				selectedLeafIndex = categoriesNames.indexOf(filterItem);
			}
		});
	}

	return (
		<React.Fragment>
			{showNoDataAvailable && (
				<NoDataAvailableContainer>
					<NoDataAvailableLabel>No data available</NoDataAvailableLabel>
				</NoDataAvailableContainer>
			)}

			<Wrapper
				columns={colCount}
				innerRef={wrapperRef}
				seriesColors={pointsColorsForSeries}
				svgElementClassName={svgElementClassName}
			>
				{chart && (
					<DetailsChartHighlightBlock
						{...props}
						left={chart.plotLeft}
						width={highlightBlockWidth}
						height={highlightBlockHeight + 36}
						selectedLeafIndex={selectedLeafIndex}
					/>
				)}
				<UnitsLabelBlock>{unitsLabel}</UnitsLabelBlock>
				<ChartWrapper key={refreshKey}>
					<HighchartsReact
						highcharts={Highcharts}
						options={chartOptions}
						ref={chartRef}
					/>
				</ChartWrapper>
			</Wrapper>
		</React.Fragment>
	);
};

export default DetailsChart;
